import type { FC } from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';

import { useComparisonProviderContext } from '../../../providers/ComparisonProvider';

import { HeatMapPicker } from './HeatMapPicker';
import {
  getHeatMapComparisonLocalStorage,
  setHeatMapComparisonLocalStorage,
} from './use-comparison-local-storage';

const useStyles = createUseStyles((theme: any) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: `calc(100vh - ${theme.spacing(30)})`,
    background: '#F0F0F0',
    overflowY: 'scroll',
  },
  helpTextContainer: {
    background: '#3D434F',
    minHeight: theme.spacing(15),
    maxHeight: theme.spacing(15),
    display: 'flex',
    alignItems: 'center',
    color: 'white',
    padding: theme.spacing(0, 5),
    fontSize: theme.fontSizes.p1,
    justifyContent: 'space-between',
    alignContent: 'center',
  },
  pickerContainer: {
    background: 'white',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  picker: {
    background: theme?.colors?.['blue-100'] || '#008B7F',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    padding: theme.spacing(12, 0),
    width: '100%',
    height: '100%',
    border: 'none',
  },
  pickerActionText: {
    paddingTop: theme.spacing(4),
    color: 'white',
    fontSize: theme.fontSizes.h4,
  },
}));

const MAX_COMPARISONS = 4;
const MIN_COMPARISONS = 2;

interface PickerFormState {
  countryCode: string;
  key: string; // Unique key to force re-mounting
}

export const HeatMapComparisonPicker: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { heatMapComparison, setHeatMapComparison } =
    useComparisonProviderContext();

  // Track the last synced version from context to avoid re-syncing unnecessarily
  const lastSyncedVersion = useRef<number | undefined>(undefined);

  // Initialize state from query string context first, then fall back to localStorage
  const [pickerStates, setPickerStates] = useState<PickerFormState[]>(() => {
    // Check if we have data from query string in context
    if (
      heatMapComparison.countryCodes &&
      heatMapComparison.countryCodes.length > 0
    ) {
      lastSyncedVersion.current = heatMapComparison.syncVersion;
      return heatMapComparison.countryCodes.map((countryCode, index) => ({
        countryCode,
        key: `picker-${Date.now()}-${index}`,
      }));
    }

    // Fall back to localStorage
    const savedStates = getHeatMapComparisonLocalStorage();
    // Filter out empty saved states (ones without a countryCode)
    const validSavedStates = savedStates.filter(saved => saved.countryCode);

    if (validSavedStates.length > 0) {
      return validSavedStates.map((saved, index) => ({
        countryCode: saved.countryCode || '',
        key: `picker-${Date.now()}-${index}`,
      }));
    }
    return [{ countryCode: '', key: `picker-${Date.now()}-0` }];
  });

  // Track confirmed state for each picker separately
  // If we have data from context or savedStates, assume they were confirmed
  const [confirmedStates, setConfirmedStates] = useState<boolean[]>(() => {
    // Check if we have data from query string in context
    if (
      heatMapComparison.countryCodes &&
      heatMapComparison.countryCodes.length > 0
    ) {
      return heatMapComparison.countryCodes.map(() => true); // All context states are confirmed
    }

    // Fall back to localStorage
    const savedStates = getHeatMapComparisonLocalStorage();
    const validSavedStates = savedStates.filter(saved => saved.countryCode);

    if (validSavedStates.length > 0) {
      return validSavedStates.map(() => true); // All valid saved states are confirmed
    }
    return [false];
  });

  // Get all selected country codes from picker states (including non-confirmed forms)
  const allSelectedCountryCodes = pickerStates
    .map(state => state.countryCode)
    .filter(Boolean);

  // Check for duplicates
  const hasDuplicates =
    allSelectedCountryCodes.length > 0 &&
    new Set(allSelectedCountryCodes).size !== allSelectedCountryCodes.length;

  // Disable generate button if less than 2 selections or any duplicates
  // Note: confirmed state no longer required - Generate will auto-confirm
  const disableGenerateButton =
    allSelectedCountryCodes.length < MIN_COMPARISONS || hasDuplicates;

  const handleFormChange = useCallback(
    (index: number, formState: Omit<PickerFormState, 'key'>) => {
      setPickerStates(prev => {
        const updated = [...prev];
        // Preserve the existing key
        updated[index] = { ...formState, key: prev[index].key };
        return updated;
      });
    },
    [],
  );

  const handleConfirm = useCallback((index: number, confirmed: boolean) => {
    setConfirmedStates(prev => {
      const updated = [...prev];
      updated[index] = confirmed;
      return updated;
    });
  }, []);

  const handleAddPicker = () => {
    if (pickerStates.length < MAX_COMPARISONS) {
      setPickerStates(prev => [
        ...prev,
        {
          countryCode: '',
          key: `picker-${Date.now()}-${prev.length}`,
        },
      ]);
      setConfirmedStates(prev => [...prev, false]);
    }
  };

  const handleRemovePicker = (index: number) => {
    setPickerStates(prev => prev.filter((_, i) => i !== index));
    setConfirmedStates(prev => prev.filter((_, i) => i !== index));
  };

  const handleGenerate = () => {
    // Auto-confirm all pickers with valid selections
    setConfirmedStates(pickerStates.map(state => !!state.countryCode));

    // Generate with all selected country codes (not just previously confirmed)
    setHeatMapComparison({
      display: true,
      countryCodes: allSelectedCountryCodes,
    });
  };

  // Save picker states to localStorage whenever they change
  // Only save states that have at least a countryCode selected
  useEffect(() => {
    const statesToSave = pickerStates
      .filter(state => state.countryCode) // Only save states with a countryCode
      .map(state => ({
        countryCode: state.countryCode,
      }));
    setHeatMapComparisonLocalStorage(statesToSave);
  }, [pickerStates]);

  // Initialize with 1 empty picker if none exist
  useEffect(() => {
    if (pickerStates.length === 0) {
      setPickerStates([{ countryCode: '', key: `picker-${Date.now()}-0` }]);
      setConfirmedStates([false]);
    }
  }, [pickerStates.length]);

  // Reinitialize picker when context syncVersion changes
  // This happens when context is updated from external sources (query string, favourites, etc.)
  useEffect(() => {
    if (
      heatMapComparison.countryCodes &&
      heatMapComparison.countryCodes.length > 0 &&
      heatMapComparison.syncVersion !== lastSyncedVersion.current
    ) {
      const contextCountryCodes = heatMapComparison.countryCodes;

      // Reinitialize from context
      const newPickerStates = contextCountryCodes.map((countryCode, index) => ({
        countryCode,
        key: `picker-${Date.now()}-${index}`,
      }));
      setPickerStates(newPickerStates);
      setConfirmedStates(contextCountryCodes.map(() => true));

      // Remember that we've synced this version
      lastSyncedVersion.current = heatMapComparison.syncVersion;
    }
  }, [heatMapComparison.syncVersion, heatMapComparison.countryCodes]);

  return (
    <div className={classes.container}>
      <div className={classes.helpTextContainer}>
        <p>{t('comparisonPicker.heatMap.helpText')}</p>
        {!disableGenerateButton && (
          <Button onClick={handleGenerate}>
            {t('comparisonPicker.heatMap.generateCta')}
          </Button>
        )}
      </div>
      <Box sx={{ margin: 4 }}>
        <Grid container spacing={2} alignItems="stretch">
          {pickerStates.map((pickerState, index) => {
            // Exclude country codes from other pickers
            const excludedCountryCodes = pickerStates
              .filter((_, i) => i !== index)
              .map(state => state.countryCode)
              .filter(Boolean);

            return (
              <Grid item xs={12} md={6} lg={3} key={pickerState.key}>
                <HeatMapPicker
                  selectionNumber={(index + 1) as 1 | 2}
                  initialCountryCode={pickerState.countryCode}
                  initialIsConfirmed={confirmedStates[index]}
                  excludedCountryCodes={excludedCountryCodes}
                  onFormChange={formState => handleFormChange(index, formState)}
                  onConfirm={confirmed => handleConfirm(index, confirmed)}
                  onRemove={
                    pickerStates.length > 1
                      ? () => handleRemovePicker(index)
                      : undefined
                  }
                />
              </Grid>
            );
          })}
          {pickerStates.length < MAX_COMPARISONS && (
            <Grid item xs={12} md={6} lg={3}>
              <div className={classes.pickerContainer}>
                <button className={classes.picker} onClick={handleAddPicker}>
                  <div
                    style={{
                      fontSize: '3rem',
                      fontWeight: 300,
                      color: 'white',
                      lineHeight: 1,
                    }}
                  >
                    +
                  </div>
                  <p className={classes.pickerActionText}>
                    Add another country
                  </p>
                </button>
              </div>
            </Grid>
          )}
        </Grid>
      </Box>
      <Box
        sx={{
          justifyContent: 'center',
          display: 'flex',
          paddingBottom: 8,
        }}
      >
        <Button
          disabled={disableGenerateButton}
          onClick={handleGenerate}
          sx={{
            mt: 2,
          }}
          color={'secondary'}
        >
          {t('comparisonPicker.heatMap.generateCta')}
        </Button>
      </Box>
    </div>
  );
};
